import React, { useEffect, useRef } from "react";
import { Container } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSceneTransitionActive,
  setHoldAndWinActive,
  setCriticalAnimationPlaying,
  updateDisplayedBalance,
} from "../../../State/actions";
import {
  selectSceneTransitionActive,
  selectHoldAndWinActive,
  selectIsHoldAndWinIntroReload,
  selectIsHoldAndWinMidFeatureReload,
} from "../../../State/selectors";
import { useAppActivity } from "../../../Contexts/AppActivityContext";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useInitialIdleAnimations, useHoldAndWinIdleAnimations } from "../../../Hooks/useLandingAnimations";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import { SOUND_NAME } from "../../../sounds";
import { soundManager } from "../../../SoundManager";
import SceneTransitionController from "./SceneTransitionController";

const SceneTransitionOverlay = () => {
  const dispatch = useDispatch();
  const { startIdleTimer } = useAppActivity();
  const { dimensions } = useDimensions();
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const { triggerHoldAndWinIdleAnimations } = useHoldAndWinIdleAnimations();
  const { backgroundAnimationControllerRef } = useReelRefs();
  const overlayRef = useRef(null);
  const controllerRef = useRef(null);
  const sceneTransitionActive = useSelector(selectSceneTransitionActive);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const isHoldAndWinIntroReload = useSelector(selectIsHoldAndWinIntroReload);
  const isHoldAndWinMidFeatureReload = useSelector(selectIsHoldAndWinMidFeatureReload);

  // Capture holdAndWinActive in a ref to avoid re-triggering effect when it changes
  const holdAndWinActiveRef = useRef(holdAndWinActive);
  holdAndWinActiveRef.current = holdAndWinActive;

  // Initialize controller
  useEffect(() => {
    if (overlayRef.current && !controllerRef.current) {
      controllerRef.current = new SceneTransitionController(
        overlayRef.current,
        dimensions
      );
      controllerRef.current.initializeSpine();
    }
  }, [dimensions]);

  // Update dimensions when they change
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.updateDimensions(dimensions);
    }
  }, [dimensions]);

  // Handle transition animation
  useEffect(() => {
    if (sceneTransitionActive && controllerRef.current) {
      // Capture current state at the time of transition
      const isEnteringHoldAndWin = !holdAndWinActiveRef.current;

      // Play appropriate transition sound when animation starts
      if (isEnteringHoldAndWin) {
        // Entering HoldAndWin - play transition_in sound
        soundManager.play(SOUND_NAME.transition_in);
      } else {
        // Exiting HoldAndWin - play transition_outro sound
        soundManager.play(SOUND_NAME.transition_outro);
      }

      // Fade out current background music at the start
      if (isEnteringHoldAndWin) {
        // Entering HoldAndWin - fade out background_music
        soundManager.fadeOut(SOUND_NAME.background_music, 500);
      }
      // Note: feature_background_music is stopped in HoldAndWinOutroController when outro finishes

      // Define midpoint callback (called when eye is fully closed)
      const midTransitionCallback = () => {
        // Clear background animations and transition win animations to idle
        const bgController = backgroundAnimationControllerRef.current;
        if (bgController?.backgroundSpine) {
          if (bgController.backgroundSpine.state) {
            bgController.backgroundSpine.state.clearTracks();
          }
          // Reset skeleton to setup pose to remove all visual elements
          if (bgController.backgroundSpine.skeleton?.setToSetupPose) {
            bgController.backgroundSpine.skeleton.setToSetupPose();
          }
        }

        if (isEnteringHoldAndWin) {
          // Entering HoldAndWin - transition any active win animations to idle
          triggerInitialIdleAnimations(true);
        }

        // Activate/deactivate hold and win feature
        if (isEnteringHoldAndWin) {
          dispatch(setHoldAndWinActive(true));
          // Play feature_idle animation for Hold and Win
          backgroundAnimationControllerRef.current?.playFeatureIdle?.();

          // CRITICAL: Trigger idle animations for BOTH natural entry AND intro reload
          // Screen is still black at this point, so animations start hidden
          // Mid-feature reload is handled by HoldAndWinReelSet (no scene transition)
          if (!isHoldAndWinMidFeatureReload) {
            // Wait 2 frames for React to mount Hold and Win components
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                triggerHoldAndWinIdleAnimations();
              });
            });
          }
        } else {
          dispatch(setHoldAndWinActive(false));
          // Play base_idle animation for regular game
          backgroundAnimationControllerRef.current?.playBaseIdle?.();

          // Trigger idle animations for base game symbols and hide textures
          // Use false to ensure textures are hidden via RAF (Mode 2)
          triggerInitialIdleAnimations(false);
        }
      };

      // Define completion callback
      const completeCallback = () => {
        // NOTE: Idle animations are triggered at midpoint (while screen is black)
        // This ensures smooth experience with no texture flash

        // Fade in appropriate background music when transition completes
        if (isEnteringHoldAndWin) {
          // Entering HoldAndWin - fade in feature_background_music
          soundManager.currentBackgroundMusic = SOUND_NAME.feature_background_music;
          soundManager.fadeIn(SOUND_NAME.feature_background_music, 500);
        } else {
          // Returning to regular game - fade in background_music
          soundManager.currentBackgroundMusic = SOUND_NAME.background_music;
          soundManager.fadeIn(SOUND_NAME.background_music, 500);
        }

        // CRITICAL: Dispatch setSceneTransitionActive(false) LAST
        // This action clears the reload flags, so animations must be triggered first
        dispatch(setSceneTransitionActive(false));
        dispatch(setCriticalAnimationPlaying(false));
        dispatch(updateDisplayedBalance()); // Sync displayedBalance to balance when transition completes
        startIdleTimer();
      };

      // Play the spine transition
      controllerRef.current.playTransition(
        midTransitionCallback,
        completeCallback
      );
    }
  }, [
    sceneTransitionActive,
    dispatch,
    startIdleTimer,
    triggerInitialIdleAnimations,
    triggerHoldAndWinIdleAnimations,
    backgroundAnimationControllerRef,
    isHoldAndWinIntroReload,
    isHoldAndWinMidFeatureReload,
  ]);

  // Always render the container so spine can be initialized
  // The spine itself will be hidden (alpha = 0.001) when not in use
  return (
    <Container
      name="scene_transition_overlay"
      ref={overlayRef}
      x={0}
      y={0}
      zIndex={10000} // Above absolutely everything
    />
  );
};

export default SceneTransitionOverlay;
